import { Charge } from "@/pages/sy/models/Charge";
import { useUpsertChargeMutation } from "@/pages/sy/hooks/queries/useChargeMutations";

type UseHeatHandlersProps = {
  currentOperatorId: number | null;
  charge: Charge | null;
  charges: Charge[];
  setHeatNameInput: (heatNameInput: string) => void;
  setIsHeatNameDialogOpen: (isHeatNameDialogOpen: boolean) => void;
  setError: (error: string) => void;
  setNotificationMessage: (message: string) => void;
  onHeatConflict?: (newHeatName: string, conflictingCharge: Charge) => void;
};

export const useHeatHandlers = ({
  charge,
  setHeatNameInput,
  setIsHeatNameDialogOpen,
  currentOperatorId,
  charges,
  setError,
  setNotificationMessage,
  onHeatConflict,
}: UseHeatHandlersProps) => {
  const upsertChargeMutation = useUpsertChargeMutation();

  const performHeatNameUpdate = (heatName: string) => {
    if (!charge?.ChargeId) return;

    const upperCaseHeatName = heatName.toUpperCase();
    const chargeToUpdate: Charge = {
      ...charge,
      ScheduledHeatName: upperCaseHeatName,
      OperatorId: currentOperatorId,
    };

    upsertChargeMutation.mutate(chargeToUpdate, {
      onSuccess: () => {
        setNotificationMessage("Scheduled heat name updated successfully");
        setIsHeatNameDialogOpen(false);
      },
      onError: (error) => {
        setError(
          "Error updating scheduled heat name: " +
            (error instanceof Error ? error.message : String(error)),
        );
      },
    });
  };

  const handleSaveHeatName = (heatName: string) => {
    if (!charge?.ChargeId) return;

    const upperCaseHeatName = heatName.toUpperCase();

    if (charge.ChargeNumber) {
      const conflictingCharge = charges.find(
        (c: Charge) =>
          c.ScheduledHeatName?.toUpperCase() === upperCaseHeatName &&
          c.ChargeNumber === charge.ChargeNumber &&
          c.ChargeId !== charge.ChargeId,
      );

      if (conflictingCharge && onHeatConflict) {
        onHeatConflict(upperCaseHeatName, conflictingCharge);
        return;
      }
    }

    performHeatNameUpdate(heatName);
  };

  const handleOpenHeatNameDialog = () => {
    if (charge?.ChargeStatusCode === "C" || charge?.ChargeStatusCode === "H")
      return;
    setHeatNameInput(charge?.ScheduledHeatName || "");
    setIsHeatNameDialogOpen(true);
  };

  const handleCloseHeatNameDialog = () => {
    setIsHeatNameDialogOpen(false);
  };

  return {
    handleSaveHeatName,
    handleOpenHeatNameDialog,
    handleCloseHeatNameDialog,
    isHeatMutating: upsertChargeMutation.isPending,
  };
};
